import { useEffect } from 'react'
import { reloadPage } from '../consent/reloadPage'
import { SET_CHANNEL_MARKUP_V2_SESSION_USER } from 'actions/actionConstants'
import {
  clearChannelMarkupV2SignInRefreshCookie,
  getChannelMarkupV2SignInRefreshCookie,
  setChannelMarkupV2SignInCookie,
  setChannelMarkupV2SignInRefreshCookie,
} from 'lib/channelMarkup/channelMarkupV2SignInCookie'
import { getChannelMarkupV2SignInKeepUserId, getChannelMarkupV2SignInRefreshUserId } from 'selectors/channelMarkupSelector'
import { useAppDispatch, useAppSelector } from './reduxHooks'

const getSessionUser = (state: App.State) => state.marketing.channelMarkupV2SessionUser

/**
 * Handles a different user signing in mid-session:
 * - With a different subscriber tier or roles, reloads the page so the server re-resolves the
 *   session user and prices them as themselves. Reloads at most once per user, so a server render
 *   that still cannot see the new account (and keeps the old session user) never loops; the next
 *   server render that can see it still re-resolves the session. The guard is cleared once the
 *   session user is that user, so a later sign-in by them can reload again.
 * - Otherwise the prices stay, and the session takes the new user's id, both here and (through
 *   the sign-in cookie) on the server. A later tier change by that same user, such as joining
 *   LuxPlus+, is then not a new sign-in and never reloads.
 */
export default function useChannelMarkupV2SignInRefresh() {
  const dispatch = useAppDispatch()
  const sessionUser = useAppSelector(getSessionUser)
  const refreshUserId = useAppSelector(getChannelMarkupV2SignInRefreshUserId)
  const keepUserId = useAppSelector(getChannelMarkupV2SignInKeepUserId)

  useEffect(() => {
    const reloadedUserId = getChannelMarkupV2SignInRefreshCookie()
    if (!refreshUserId) {
      if (reloadedUserId && reloadedUserId === sessionUser?.userId) {
        clearChannelMarkupV2SignInRefreshCookie()
      }
      return
    }
    if (reloadedUserId === refreshUserId) {
      return
    }
    setChannelMarkupV2SignInRefreshCookie(refreshUserId)
    reloadPage()
  }, [refreshUserId, sessionUser?.userId])

  useEffect(() => {
    if (!keepUserId || !sessionUser) {
      return
    }
    setChannelMarkupV2SignInCookie(keepUserId)
    dispatch({ type: SET_CHANNEL_MARKUP_V2_SESSION_USER, sessionUser: { ...sessionUser, userId: keepUserId } })
  }, [dispatch, keepUserId, sessionUser])
}
